import React from "react";

const FONT   = "'Inter','Avenir Next','Century Gothic',sans-serif";
const TEAL   = "#4fd1c5";
const BG     = "#0f1117";
const PANEL  = "#161b24";
const BORDER = "#2d3748";
const TEXT   = "#e8e6e1";
const MUTED  = "#708898";

interface TermsPageProps {
  onBack?: () => void;
}

// ── Reusable layout components ───────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{
      fontSize: 12, fontWeight: 700, color: TEAL,
      margin: "40px 0 10px", textTransform: "uppercase", letterSpacing: "0.06em",
    }}>
      {children}
    </h2>
  );
}

function BodyText({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{
      fontSize: 14, color: MUTED, lineHeight: 1.75,
      margin: "0 0 14px", ...style,
    }}>
      {children}
    </p>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>;
}

function Divider() {
  return <div style={{ borderTop: `1px solid ${BORDER}`, margin: "36px 0" }} />;
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ margin: "0 0 14px", paddingLeft: 20 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 14, color: MUTED, lineHeight: 1.75, marginBottom: 6 }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

function AlertBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: PANEL, border: `1px solid ${BORDER}`,
      borderRadius: 8, padding: "16px 20px", marginBottom: 14,
    }}>
      <p style={{ fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.7 }}>{children}</p>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function TermsPage({ onBack }: TermsPageProps) {
  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: FONT, color: TEXT }}>

      {/* ── Nav bar ── */}
      <nav style={{
        borderBottom: `1px solid ${BORDER}`, padding: "14px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, background: BG, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            onClick={onBack}
            style={{ fontSize: 17, fontWeight: 800, cursor: onBack ? "pointer" : "default", userSelect: "none" }}
            title={onBack ? "Home" : undefined}
          >
            <span style={{ color: TEXT }}>Open</span>
            <span style={{ color: TEAL }}>Estimate</span>
          </span>
          <span style={{ color: BORDER, fontSize: 16, margin: "0 4px" }}>/</span>
          <span style={{ fontSize: 13, color: MUTED, fontWeight: 500 }}>Terms of Service</span>
        </div>
        {onBack && (
          <button onClick={onBack} style={{
            background: "none", border: `1px solid ${BORDER}`, color: MUTED,
            borderRadius: 6, padding: "6px 16px", fontSize: 13, cursor: "pointer",
            fontFamily: FONT,
          }}>
            ← Back
          </button>
        )}
      </nav>

      {/* ── Body ── */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "56px 32px 80px" }}>

        {/* Hero */}
        <div style={{ marginBottom: 48 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: TEAL + "15", border: `1px solid ${TEAL}30`,
            borderRadius: 999, padding: "6px 16px", marginBottom: 24,
          }}>
            <span style={{ fontSize: 12, color: TEAL, fontWeight: 600, letterSpacing: "0.06em" }}>
              TERMS OF SERVICE
            </span>
          </div>
          <h1 style={{
            fontSize: 36, fontWeight: 800, letterSpacing: "-0.03em",
            margin: "0 0 16px", lineHeight: 1.15, color: TEXT,
          }}>
            Plain-language terms.<br />
            <span style={{ color: TEAL }}>No surprises.</span>
          </h1>
          <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.7, maxWidth: 600, margin: 0 }}>
            These Terms of Service ("Terms") govern your use of <Term>OpenEstimate</Term>,
            a cloud-based construction cost estimation and project management platform
            operated by <Term>Alex Bruce</Term>. By creating an account or using the
            service, you agree to these Terms.
          </p>
          <p style={{ fontSize: 13, color: MUTED + "99", marginTop: 16, marginBottom: 0 }}>
            Last updated: <Term>April 2026</Term>
          </p>
        </div>

        <AlertBox>
          <Term>Key points:</Term> You own your project data. We don't claim ownership of it.
          Our liability is capped at fees paid in the prior 12 months. Disputes under $10,000
          go to binding arbitration. You can cancel anytime — annual plan subscribers receive
          a pro-rata refund.
        </AlertBox>

        {/* 1. Acceptance */}
        <SectionHeading>1. Acceptance of Terms</SectionHeading>
        <BodyText>
          By accessing or using OpenEstimate — including creating an account, importing a project,
          or using the AI import feature — you confirm that you have read, understood, and agree
          to be bound by these Terms and our{" "}
          <a href="#" style={{ color: TEAL, textDecoration: "none" }}>Privacy Policy</a>.
          If you are using the service on behalf of an organization, you represent that you have
          authority to bind that organization to these Terms.
        </BodyText>
        <BodyText>
          If you do not agree to these Terms, you may not use the service.
        </BodyText>

        <Divider />

        {/* 2. Service Description */}
        <SectionHeading>2. Service Description</SectionHeading>
        <BodyText>
          OpenEstimate is a <Term>cloud-based pre-construction cost estimation and
          project management</Term> software platform. The service enables construction
          professionals to create and manage cost estimates using CSI MasterFormat divisions,
          generate bid comparisons, track project snapshots, and collaborate with project teams.
        </BodyText>
        <BodyText>
          The service is provided on a subscription basis, hosted on Vercel and Supabase infrastructure,
          and accessible via the web application at{" "}
          <a href="https://open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            open-estimate.com
          </a>. We reserve the right to modify, suspend, or discontinue features with reasonable
          notice to subscribers.
        </BodyText>

        <Divider />

        {/* 3. Account Requirements */}
        <SectionHeading>3. Account Requirements</SectionHeading>
        <BulletList items={[
          <><Term>Age requirement:</Term> You must be at least 18 years old to create an account. By registering, you represent that you meet this requirement.</>,
          <><Term>One account per person:</Term> Each individual user may maintain only one account. Creating duplicate accounts to circumvent subscription limits or access restrictions is a violation of these Terms.</>,
          <><Term>Account security:</Term> You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. Notify us immediately at <a href="mailto:legal@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>legal@open-estimate.com</a> if you suspect unauthorized access.</>,
          <><Term>Accurate information:</Term> You agree to provide accurate, current, and complete information when creating your account and to keep it updated.</>,
        ]} />

        <Divider />

        {/* 4. Acceptable Use */}
        <SectionHeading>4. Acceptable Use</SectionHeading>
        <BodyText>
          OpenEstimate is provided for <Term>legitimate construction industry use only</Term>.
          You agree not to:
        </BodyText>
        <BulletList items={[
          <>Resell, sublicense, or provide access to the service to third parties without written authorization.</>,
          <>Scrape, crawl, or programmatically extract data from the platform beyond what the documented API permits.</>,
          <>Reverse engineer, decompile, disassemble, or attempt to derive the source code of any part of the service.</>,
          <>Upload malicious code, conduct denial-of-service attacks, or attempt to probe or penetrate our security systems.</>,
          <>Use the service for any unlawful purpose or in violation of any applicable regulations, including export controls.</>,
          <>Impersonate another person or organization, or misrepresent your affiliation with any entity.</>,
          <>Circumvent any access controls, billing systems, or usage limits imposed by the service.</>,
        ]} />
        <BodyText>
          Violation of this section may result in immediate account suspension without refund.
        </BodyText>

        <Divider />

        {/* 5. Your Data */}
        <SectionHeading>5. Your Data</SectionHeading>
        <BodyText>
          <Term>You own your project data.</Term> All estimate content, cost figures, schedules,
          notes, and other project data you create in OpenEstimate remains your intellectual
          property. We do not claim any ownership rights over your data.
        </BodyText>
        <BodyText>
          By using the service, you grant OpenEstimate a limited, non-exclusive, worldwide,
          royalty-free license to <Term>host, store, process, and transmit</Term> your project data
          solely as necessary to provide the service to you. This license terminates when you
          delete your account and your data is purged from our systems.
        </BodyText>
        <BodyText>
          We will not access, analyze, or use your specific project data (cost figures, project names,
          client details) for any purpose other than providing and maintaining the service, except
          as required by law or as described in our Privacy Policy.
        </BodyText>

        <Divider />

        {/* 6. Our Intellectual Property */}
        <SectionHeading>6. Our Intellectual Property</SectionHeading>
        <BodyText>
          The OpenEstimate software, user interface, visual design, calculation algorithms,
          database schema, documentation, and brand (including the name "OpenEstimate" and logo)
          are the proprietary intellectual property of Alex Bruce / OpenEstimate, protected
          by copyright, trade secret, and other applicable laws.
        </BodyText>
        <BodyText>
          These Terms grant you a limited, non-exclusive, non-transferable license to use the
          service for your own construction business purposes. No other rights are granted.
          You may not copy, modify, distribute, or create derivative works from any part of the
          OpenEstimate software or UI.
        </BodyText>

        <Divider />

        {/* 7. Subscription and Payment */}
        <SectionHeading>7. Subscription and Payment</SectionHeading>
        <BulletList items={[
          <><Term>Fees:</Term> Subscription fees are as listed on the <a href="https://open-estimate.com/pricing" style={{ color: TEAL, textDecoration: "none" }}>pricing page</a> at the time of purchase. We may update pricing with 30 days' notice to existing subscribers.</>,
          <><Term>Billing cycle:</Term> Subscriptions are billed monthly or annually, beginning on the date of purchase. Your subscription renews automatically at the end of each billing period.</>,
          <><Term>Auto-renewal:</Term> By subscribing, you authorize us to charge your payment method on file at the start of each renewal period. You may cancel auto-renewal at any time before the renewal date.</>,
          <><Term>Annual plan refunds:</Term> If you cancel an annual subscription, you are entitled to a pro-rata refund for the unused portion of your subscription term (calculated by full months remaining), less any applicable transaction fees. Monthly subscriptions are not refunded for partial months.</>,
          <><Term>Failed payments:</Term> If a payment fails, we will retry up to 3 times over 7 days. After this period, your account may be downgraded to a read-only state until payment is resolved.</>,
          <><Term>Taxes:</Term> Stated prices are exclusive of applicable taxes. You are responsible for any sales, VAT, or use taxes applicable to your jurisdiction.</>,
        ]} />

        <Divider />

        {/* 8. Data on Cancellation */}
        <SectionHeading>8. Your Data Upon Cancellation</SectionHeading>
        <BodyText>
          When you cancel your subscription, your account enters a <Term>30-day grace period</Term>{" "}
          during which your projects remain accessible in a read-only state for export. We
          strongly encourage you to export your data (Settings → Privacy &amp; Data → Export Data)
          before the grace period ends.
        </BodyText>
        <BodyText>
          After the 30-day grace period, your project data is <Term>permanently and irreversibly
          deleted</Term> from our systems. We are not responsible for data loss resulting from
          failure to export data before the grace period expires.
        </BodyText>

        <Divider />

        {/* 9. No Warranty */}
        <SectionHeading>9. Disclaimer of Warranties</SectionHeading>
        <AlertBox>
          <Term>The service is provided "as is" and "as available" without warranties of any kind,
          express or implied.</Term>
        </AlertBox>
        <BodyText>
          OpenEstimate expressly disclaims all warranties, including but not limited to implied
          warranties of merchantability, fitness for a particular purpose, and non-infringement.
          We do not warrant that:
        </BodyText>
        <BulletList items={[
          <>The service will be uninterrupted, error-free, or available at any particular time.</>,
          <><Term>Cost estimates, calculations, or AI-generated outputs are accurate, complete, or suitable for use in binding contracts.</Term> Estimates are tools to assist professional judgment — not substitutes for it. Always verify figures independently before using them in bids or contracts.</>,
          <>The service will meet your specific business requirements.</>,
          <>Any defects will be corrected within a particular timeframe.</>,
        ]} />

        <Divider />

        {/* 10. Limitation of Liability */}
        <SectionHeading>10. Limitation of Liability</SectionHeading>
        <BodyText>
          To the maximum extent permitted by applicable law:
        </BodyText>
        <BulletList items={[
          <><Term>Cap on liability:</Term> OpenEstimate's total cumulative liability to you for any claims arising out of or related to these Terms or the service — regardless of the form of action — is capped at the <Term>total fees you paid to OpenEstimate in the 12 months immediately preceding the event giving rise to the claim</Term>.</>,
          <><Term>No consequential damages:</Term> Neither party will be liable for any indirect, incidental, special, consequential, or punitive damages — including loss of profits, loss of data, loss of business, or cost of substitute services — even if advised of the possibility of such damages.</>,
          <><Term>Essential basis:</Term> These limitations reflect an allocation of risk between the parties and are an essential basis of the bargain between you and OpenEstimate. The service would not be offered at current pricing without these limitations.</>,
        ]} />
        <BodyText>
          Some jurisdictions do not allow limitation of certain damages. In those jurisdictions,
          our liability will be limited to the greatest extent permitted by law.
        </BodyText>

        <Divider />

        {/* 11. Indemnification */}
        <SectionHeading>11. Indemnification</SectionHeading>
        <BodyText>
          You agree to indemnify, defend, and hold harmless OpenEstimate, Alex Bruce, and any
          officers, employees, contractors, or agents from and against any claims, damages,
          losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of:
        </BodyText>
        <BulletList items={[
          <>Your violation of these Terms or any applicable law or regulation.</>,
          <>Your use of the service in a manner not authorized by these Terms.</>,
          <>Your project data, including any claims that it infringes a third party's intellectual property rights.</>,
          <>Any dispute between you and a third party (e.g., a client, subcontractor, or project owner) arising from an estimate generated using OpenEstimate.</>,
        ]} />

        <Divider />

        {/* 12. Termination */}
        <SectionHeading>12. Termination</SectionHeading>
        <BodyText>
          <Term>By you:</Term> You may cancel your subscription at any time via Settings →
          Billing → Cancel Subscription. Cancellation takes effect at the end of the current
          billing period (monthly) or triggers a pro-rata refund (annual). You may also request
          immediate account deletion via Settings → Privacy &amp; Data → Delete Account.
        </BodyText>
        <BodyText>
          <Term>By us:</Term> We may suspend or terminate your account with <Term>30 days' written notice</Term>{" "}
          for any reason, or <Term>immediately</Term> for violations of Section 4 (Acceptable Use),
          non-payment after the cure period, or actions that pose a security risk to the platform
          or other users. In the event of termination by us without cause, we will provide a
          pro-rata refund for any prepaid unused subscription period.
        </BodyText>
        <BodyText>
          Upon termination, your license to use the service is revoked and Sections 5 (Your Data),
          6 (Our IP), 9 (Warranties), 10 (Liability), 11 (Indemnification), 13 (Governing Law),
          and 14 (Dispute Resolution) survive.
        </BodyText>

        <Divider />

        {/* 13. Governing Law */}
        <SectionHeading>13. Governing Law</SectionHeading>
        <BodyText>
          These Terms are governed by and construed in accordance with the laws of the{" "}
          <Term>State of Delaware, United States</Term>, without regard to its conflict of law
          provisions. For disputes not subject to arbitration under Section 14, you consent to
          exclusive jurisdiction in the state and federal courts located in Delaware.
        </BodyText>

        <Divider />

        {/* 14. Dispute Resolution */}
        <SectionHeading>14. Dispute Resolution and Arbitration</SectionHeading>
        <BodyText>
          <Term>Informal resolution first:</Term> Before initiating any formal proceeding, the
          parties agree to attempt to resolve any dispute informally by contacting{" "}
          <a href="mailto:legal@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            legal@open-estimate.com
          </a>{" "}
          and allowing 30 days for good-faith resolution.
        </BodyText>
        <BodyText>
          <Term>Binding arbitration for disputes under $10,000:</Term> For any dispute or claim
          where the total amount sought is less than $10,000 USD, the dispute will be resolved
          by final and binding arbitration administered by the American Arbitration Association
          (AAA) under its Consumer Arbitration Rules, as a single arbitrator proceeding, conducted
          via written submissions unless either party requests otherwise. Judgment on the award
          may be entered in any court of competent jurisdiction.
        </BodyText>
        <BodyText>
          <Term>Class action waiver:</Term> You and OpenEstimate agree that each may bring claims
          against the other only in an individual capacity, and not as a plaintiff or class member
          in any purported class, collective, or representative proceeding.
        </BodyText>
        <BodyText>
          <Term>Exceptions:</Term> Either party may seek emergency injunctive or equitable relief
          in a court of competent jurisdiction to prevent irreparable harm pending arbitration.
          Disputes over $10,000 may be litigated in the courts specified in Section 13.
        </BodyText>

        <Divider />

        {/* 15. Changes */}
        <SectionHeading>15. Changes to These Terms</SectionHeading>
        <BodyText>
          We may update these Terms from time to time. For <Term>material changes</Term> — those
          that meaningfully affect your rights or obligations — we will provide at least{" "}
          <Term>30 days' written notice</Term> by email to your account address before the
          changes take effect.
        </BodyText>
        <BodyText>
          If you do not agree to the updated Terms, you may cancel your subscription before the
          effective date and receive a pro-rata refund of any prepaid fees. Continued use after
          the effective date constitutes acceptance of the revised Terms.
        </BodyText>
        <BodyText>
          Non-material changes (e.g., clarifications, typo corrections, reordering of sections)
          may be made without advance notice. The "Last updated" date above reflects the most
          recent revision.
        </BodyText>

        <Divider />

        {/* 16. General */}
        <SectionHeading>16. General Provisions</SectionHeading>
        <BulletList items={[
          <><Term>Entire agreement:</Term> These Terms, together with the Privacy Policy and any order forms or subscription confirmations, constitute the entire agreement between you and OpenEstimate regarding the service.</>,
          <><Term>Severability:</Term> If any provision of these Terms is found unenforceable, that provision will be modified to the minimum extent necessary to make it enforceable, and the remaining provisions will continue in full force.</>,
          <><Term>No waiver:</Term> Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.</>,
          <><Term>Assignment:</Term> You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights and obligations under these Terms in connection with a merger, acquisition, or sale of assets.</>,
          <><Term>Force majeure:</Term> Neither party will be liable for failure to perform due to causes beyond their reasonable control, including natural disasters, government actions, or third-party infrastructure failures.</>,
        ]} />

        <Divider />

        {/* Contact */}
        <div style={{
          background: TEAL + "0d", border: `1px solid ${TEAL}30`,
          borderRadius: 10, padding: "28px 32px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 20,
        }}>
          <div>
            <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: TEXT }}>
              Legal questions?
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: MUTED, maxWidth: 480, lineHeight: 1.6 }}>
              For questions about these Terms, enterprise agreements, or to report
              a violation, contact our legal team. We respond within 2 business days.
            </p>
          </div>
          <a
            href="mailto:legal@open-estimate.com"
            style={{
              display: "inline-block", background: TEAL, color: BG,
              fontWeight: 700, fontSize: 13, textDecoration: "none",
              padding: "10px 22px", borderRadius: 6, whiteSpace: "nowrap",
              fontFamily: FONT,
            }}
          >
            legal@open-estimate.com
          </a>
        </div>

        <p style={{ fontSize: 12, color: MUTED, textAlign: "center", marginTop: 40, opacity: 0.6 }}>
          Last updated April 2026 · OpenEstimate is operated by Alex Bruce ·{" "}
          <a href="mailto:legal@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            legal@open-estimate.com
          </a>
        </p>

      </div>
    </div>
  );
}
